import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Users, TrendingUp, FileText, DollarSign, Download, Search,
  ChevronLeft, ChevronRight, UserPlus, Crown, Building2, Gift,
  Eye, MoreVertical, RefreshCw, Trash2, AlertTriangle, X, BookOpen,
  Tag, Percent, Calendar, Plus, Mail, Clock, CheckCircle, XCircle, Ban, Activity, Send, Zap, Settings, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [addStudiesAmount, setAddStudiesAmount] = useState(1);
  const [userStudies, setUserStudies] = useState([]);
  const [showStudiesModal, setShowStudiesModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loadingStudies, setLoadingStudies] = useState(false);
  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showLinkStripeModal, setShowLinkStripeModal] = useState(false);
  const [selectedPromoForLink, setSelectedPromoForLink] = useState(null);
  const [stripeCouponId, setStripeCouponId] = useState('');
  const [showDeletePromoModal, setShowDeletePromoModal] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);
  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: 10,
    max_uses: null,
    expires_at: '',
    applicable_plans: ['pro', 'agency'],
    studies_limit: null,  // Pour no_cb: nombre d'études (null = défaut du plan)
    learnybox_sequence_id: ''  // Optionnel: séquence LearnyBox
  });
  // Scheduled emails state
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [emailStats, setEmailStats] = useState({ pending: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });
  const [emailStatusFilter, setEmailStatusFilter] = useState('all');
  // Diagnostic sessions state
  const [diagnosticSessions, setDiagnosticSessions] = useState([]);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [cancellationsData, setCancellationsData] = useState(null);
  const [loadingCancellations, setLoadingCancellations] = useState(false);
  const [resendingPdf, setResendingPdf] = useState(null);
  // Admin Studies list state
  const [studiesList, setStudiesList] = useState([]);
  const [loadingStudiesList, setLoadingStudiesList] = useState(false);
  const [sendingStudyPdf, setSendingStudyPdf] = useState(null);
  // Gift subscription state
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftData, setGiftData] = useState({
    email: '',
    plan: 'pro',
    duration_days: 30,
    studies: 5
  });
  const [giftLoading, setGiftLoading] = useState(false);
  // Avatar trial sessions state
  const [avatarTrials, setAvatarTrials] = useState([]);
  const [loadingAvatarTrials, setLoadingAvatarTrials] = useState(false);
  // Offer quiz sessions state
  const [offerQuizSessions, setOfferQuizSessions] = useState([]);
  const [offerQuizStats, setOfferQuizStats] = useState({});
  const [loadingOfferQuiz, setLoadingOfferQuiz] = useState(false);
  // Trial No CB state
  const [trialNoCBUsers, setTrialNoCBUsers] = useState([]);
  const [loadingTrialNoCB, setLoadingTrialNoCB] = useState(false);
  // Fix promo user state
  const [showFixPromoModal, setShowFixPromoModal] = useState(false);
  const [fixPromoData, setFixPromoData] = useState({ email: '', studies_limit: 2 });
  const [fixPromoLoading, setFixPromoLoading] = useState(false);
  // Search user state
  const [showSearchUserModal, setShowSearchUserModal] = useState(false);
  const [searchUserEmail, setSearchUserEmail] = useState('');
  const [searchUserResult, setSearchUserResult] = useState(null);
  const [searchUserLoading, setSearchUserLoading] = useState(false);
  const [sendingAllPdfs, setSendingAllPdfs] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [statsRes, usersRes, txRes, promoRes] = await Promise.all([
        axios.get(`${API}/admin/stats/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/revenue/transactions?limit=10`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/promo-codes`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { promo_codes: [] } }))
      ]);
      setStats(statsRes.data.overview);
      setUsers(usersRes.data.users);
      setTransactions(txRes.data.transactions);
      setPromoCodes(promoRes.data.promo_codes || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Accès non autorisé');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams();
      if (filterPlan !== 'all') params.append('filter_plan', filterPlan);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '50');
      
      const res = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    }
  };

  const loadScheduledEmails = async (status = null) => {
    const token = localStorage.getItem('token');
    try {
      const params = status && status !== 'all' ? `?status=${status}` : '';
      const res = await axios.get(`${API}/admin/scheduled-emails${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledEmails(res.data.emails || []);
      setEmailStats(res.data.stats || { pending: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });
    } catch (error) {
      console.error('Error loading scheduled emails:', error);
      toast.error('Erreur lors du chargement des emails');
    }
  };

  const loadCancellations = async () => {
    const token = localStorage.getItem('token');
    setLoadingCancellations(true);
    try {
      const res = await axios.get(`${API}/admin/cancellations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancellationsData(res.data);
    } catch (error) {
      console.error('Error loading cancellations:', error);
      toast.error('Erreur lors du chargement des annulations');
    } finally {
      setLoadingCancellations(false);
    }
  };

  const loadDiagnosticSessions = async () => {
    const token = localStorage.getItem('token');
    setLoadingDiagnostics(true);
    try {
      const res = await axios.get(`${API}/admin/diagnostic-sessions?status=completed&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnosticSessions(res.data.sessions || []);
    } catch (error) {
      console.error('Error loading diagnostic sessions:', error);
      toast.error('Erreur lors du chargement des diagnostics');
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const resendDiagnosticPdf = async (sessionId, email) => {
    const token = localStorage.getItem('token');
    setResendingPdf(sessionId);
    try {
      await axios.post(`${API}/admin/diagnostic/${sessionId}/resend-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`PDF renvoyé à ${email}`);
    } catch (error) {
      console.error('Error resending PDF:', error);
      toast.error('Erreur lors de l\'envoi du PDF');
    } finally {
      setResendingPdf(null);
    }
  };

  const loadStudiesList = async () => {
    const token = localStorage.getItem('token');
    setLoadingStudiesList(true);
    try {
      const res = await axios.get(`${API}/admin/studies-list?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudiesList(res.data.studies || []);
    } catch (error) {
      console.error('Error loading studies:', error);
      toast.error('Erreur lors du chargement des études');
    } finally {
      setLoadingStudiesList(false);
    }
  };

  const loadAvatarTrials = async () => {
    const token = localStorage.getItem('token');
    setLoadingAvatarTrials(true);
    try {
      const res = await axios.get(`${API}/admin/avatar-trial-sessions?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvatarTrials(res.data.sessions || []);
    } catch (error) {
      console.error('Error loading avatar trials:', error);
      toast.error('Erreur lors du chargement des essais gratuits');
    } finally {
      setLoadingAvatarTrials(false);
    }
  };

  const loadOfferQuizSessions = async () => {
    const token = localStorage.getItem('token');
    setLoadingOfferQuiz(true);
    try {
      const res = await axios.get(`${API}/admin/offer-quiz-sessions?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOfferQuizSessions(res.data.sessions || []);
      setOfferQuizStats(res.data.stats || {});
    } catch (error) {
      console.error('Error loading offer quiz sessions:', error);
      toast.error('Erreur lors du chargement des quiz Offre');
    } finally {
      setLoadingOfferQuiz(false);
    }
  };

  const loadTrialNoCBUsers = async () => {
    const token = localStorage.getItem('token');
    setLoadingTrialNoCB(true);
    try {
      const res = await axios.get(`${API}/admin/trial-no-cb-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialNoCBUsers(res.data.users || []);
    } catch (error) {
      console.error('Error loading trial no CB users:', error);
      toast.error('Erreur lors du chargement des essais sans CB');
    } finally {
      setLoadingTrialNoCB(false);
    }
  };


  const sendStudyPdf = async (studyId, email) => {
    const token = localStorage.getItem('token');
    setSendingStudyPdf(studyId);
    try {
      await axios.post(`${API}/admin/study/${studyId}/send-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`PDF envoyé à ${email}`);
    } catch (error) {
      console.error('Error sending study PDF:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du PDF');
    } finally {
      setSendingStudyPdf(null);
    }
  };

  const triggerEmailProcessing = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/scheduled-emails/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Traitement des emails déclenché');
      loadScheduledEmails(emailStatusFilter);
    } catch (error) {
      toast.error('Erreur lors du traitement');
    }
  };

  const cancelUserEmails = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.delete(`${API}/admin/scheduled-emails/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.cancelled_count} email(s) annulé(s)`);
      loadScheduledEmails(emailStatusFilter);
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const handleAddStudies = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/admin/users/${userId}`, 
        { add_studies: addStudiesAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${addStudiesAmount} étude(s) ajoutée(s) avec succès`);
      loadUsers();
      setShowUserModal(false);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout des études');
    }
  };

  const handleChangePlan = async (userId, newPlan) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/admin/users/${userId}`,
        { plan: newPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Plan mis à jour avec succès');
      loadUsers();
      setShowUserModal(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du plan');
    }
  };

  const loadUserStudies = async (userId) => {
    const token = localStorage.getItem('token');
    setLoadingStudies(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/studies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStudies(res.data.studies || []);
      setShowStudiesModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des études');
    } finally {
      setLoadingStudies(false);
    }
  };

  const handleDeleteStudy = async (userId, studyId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/admin/users/${userId}/studies/${studyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Étude supprimée avec succès');
      // Refresh studies list
      loadUserStudies(userId);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAllUserData = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/admin/users/${userId}/all-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Toutes les données utilisateur ont été supprimées');
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      setShowUserModal(false);
      setShowStudiesModal(false);
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Gift subscription function
  const handleGiftSubscription = async () => {
    if (!giftData.email.trim()) {
      toast.error('Veuillez entrer un email');
      return;
    }
    
    setGiftLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await axios.post(`${API}/admin/users/gift-subscription`, giftData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`🎁 Abonnement ${giftData.plan.toUpperCase()} offert à ${giftData.email} pour ${giftData.duration_days} jours !`);
      setShowGiftModal(false);
      setGiftData({ email: '', plan: 'pro', duration_days: 30, studies: 5 });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'attribution de l\'abonnement');
    } finally {
      setGiftLoading(false);
    }
  };

  // Fix promo user function
  const handleFixPromoUser = async () => {
    if (!fixPromoData.email.trim()) {
      toast.error('Veuillez entrer l\'email de l\'utilisateur');
      return;
    }
    
    setFixPromoLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/admin/fix-promo-user`, fixPromoData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowFixPromoModal(false);
      setFixPromoData({ email: '', studies_limit: 2 });
      loadTrialNoCBUsers(); // Rafraîchir la liste
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    } finally {
      setFixPromoLoading(false);
    }
  };

  // Search user by email
  const handleSearchUser = async () => {
    if (!searchUserEmail.trim()) {
      toast.error('Veuillez entrer un email');
      return;
    }
    
    setSearchUserLoading(true);
    setSearchUserResult(null);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/users/by-email/${encodeURIComponent(searchUserEmail.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchUserResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Utilisateur non trouvé');
      setSearchUserResult(null);
    } finally {
      setSearchUserLoading(false);
    }
  };

  // Send all PDFs to user
  const handleSendAllPdfs = async () => {
    if (!searchUserEmail.trim()) return;
    
    setSendingAllPdfs(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/admin/users/by-email/${encodeURIComponent(searchUserEmail.trim())}/send-all-pdfs`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.message}. Problèmes: ${response.data.errors.join(', ')}`);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi des PDFs');
    } finally {
      setSendingAllPdfs(false);
    }
  };

  // Fix paid user data (clean old trial flags)
  const handleFixPaidUser = async () => {
    if (!searchUserEmail.trim()) return;
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/admin/users/by-email/${encodeURIComponent(searchUserEmail.trim())}/fix-paid-user`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      // Refresh user data
      handleSearchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    }
  };

  // Promo codes functions
  const loadPromoCodes = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(res.data.promo_codes || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des codes promo');
    }
  };

  const handleCreatePromoCode = async () => {
    const token = localStorage.getItem('token');
    
    if (!newPromoCode.code.trim()) {
      toast.error('Veuillez entrer un code');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        ...newPromoCode,
        code: newPromoCode.code.toUpperCase(),
        expires_at: newPromoCode.expires_at || null,
        max_uses: newPromoCode.max_uses || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Code promo ${newPromoCode.code.toUpperCase()} créé !`);
      setShowPromoModal(false);
      setNewPromoCode({
        code: '',
        discount_type: 'percent',
        discount_value: 10,
        max_uses: null,
        expires_at: '',
        applicable_plans: ['pro', 'agency']
      });
      loadPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleTogglePromoCode = async (codeId, isActive) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/admin/promo-codes/${codeId}`, 
        { is_active: !isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isActive ? 'Code désactivé' : 'Code activé');
      loadPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeletePromoCode = async (codeId, codeName) => {
    // Ouvrir le modal de confirmation au lieu de confirm()
    setPromoToDelete({ id: codeId, code: codeName });
    setShowDeletePromoModal(true);
  };

  const confirmDeletePromoCode = async () => {
    if (!promoToDelete) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/admin/promo-codes/${promoToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Code promo ${promoToDelete.code} supprimé`);
      loadPromoCodes();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setShowDeletePromoModal(false);
      setPromoToDelete(null);
    }
  };

  const handleLinkStripe = async () => {
    if (!selectedPromoForLink || !stripeCouponId.trim()) {
      toast.error('Veuillez entrer un ID de coupon Stripe');
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/admin/promo-codes/${selectedPromoForLink.id}`, 
        { stripe_coupon_id: stripeCouponId.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Coupon Stripe lié au code ${selectedPromoForLink.code}`);
      setShowLinkStripeModal(false);
      setStripeCouponId('');
      setSelectedPromoForLink(null);
      loadPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la liaison');
    }
  };

  const handleExport = async (filterType) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/admin/users/export?filter_type=${filterType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create CSV
      const csvData = res.data.users;
      const headers = ['email', 'firstname', 'lastname', 'plan', 'status', 'created_at', 'tags'];
      const csvContent = [
        headers.join(','),
        ...csvData.map(u => [
          u.email,
          u.firstname,
          u.lastname,
          u.plan,
          u.status,
          u.created_at,
          u.tags.join(';')
        ].map(v => `"${v || ''}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `boost-strategia-${filterType}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`${res.data.count} utilisateurs exportés`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [filterPlan, searchTerm, activeTab]);

  const getPlanBadge = (plan) => {
    if (plan === 'lead_avatar') {
      return <span className="px-2 py-1 rounded-full text-xs bg-pink-500/20 text-pink-400">Lead Avatar</span>;
    } else if (!plan || plan === 'free') {
      return <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">Gratuit</span>;
    } else if (plan.includes('pro')) {
      return <span className="px-2 py-1 rounded-full text-xs bg-primary/20 text-primary">Pro</span>;
    } else if (plan.includes('agency')) {
      return <span className="px-2 py-1 rounded-full text-xs bg-accent/20 text-accent">Agence</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20">{plan}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <Crown className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold">Administration</h1>
              <p className="text-sm text-muted">Boost-StrategIA</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Retour au Dashboard
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6 overflow-x-auto">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
              { id: 'users', label: 'Utilisateurs', icon: Users },
              { id: 'studies', label: 'Études', icon: BookOpen },
              { id: 'diagnostics', label: 'Diagnostics', icon: Activity },
              { id: 'trialNoCB', label: 'Essai sans CB', icon: Clock },
              { id: 'cancellations', label: 'Annulations', icon: XCircle },
              { id: 'avatarTrials', label: 'LM Avatar', icon: Gift },
              { id: 'offerQuiz', label: 'LM Offre', icon: Zap },
              { id: 'emails', label: 'Emails', icon: Mail },
              { id: 'promo', label: 'Codes Promo', icon: Tag },
              { id: 'export', label: 'Export CRM', icon: Download }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'emails') loadScheduledEmails(emailStatusFilter);
                  if (tab.id === 'diagnostics') loadDiagnosticSessions();
                  if (tab.id === 'studies') loadStudiesList();
                  if (tab.id === 'avatarTrials') loadAvatarTrials();
                  if (tab.id === 'offerQuiz') loadOfferQuizSessions();
                  if (tab.id === 'trialNoCB') loadTrialNoCBUsers();
                  if (tab.id === 'cancellations') loadCancellations();
                }}
                className={`flex items-center gap-2 py-4 px-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="p-5 border-border">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted">Total Utilisateurs</span>
                </div>
                <p className="text-3xl font-bold">{stats.total_users}</p>
                <p className="text-xs text-muted mt-1">+{stats.recent_signups} cette semaine</p>
              </Card>
              
              <Card className="p-5 border-border">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-highlight" />
                  <span className="text-sm text-muted">MRR</span>
                </div>
                <p className="text-3xl font-bold">{stats.mrr}€</p>
                <p className="text-xs text-muted mt-1">Revenus récurrents mensuels</p>
              </Card>
              
              <Card className="p-5 border-border">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="text-sm text-muted">Études</span>
                </div>
                <p className="text-3xl font-bold">{stats.total_studies}</p>
                <p className="text-xs text-muted mt-1">{stats.completed_studies} terminées</p>
              </Card>
              
              <Card className="p-5 border-border">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted">Revenus 30j</span>
                </div>
                <p className="text-3xl font-bold">{stats.recent_revenue}€</p>
                <p className="text-xs text-muted mt-1">Derniers 30 jours</p>
              </Card>
            </div>

            {/* User breakdown */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="p-6 border-border">
                <h3 className="font-heading font-semibold mb-4">Répartition des utilisateurs</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      <span>Gratuits</span>
                    </div>
                    <span className="font-semibold">{stats.free_users}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      <span>Pro</span>
                    </div>
                    <span className="font-semibold">{stats.pro_users}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-accent"></div>
                      <span>Agence</span>
                    </div>
                    <span className="font-semibold">{stats.agency_users}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-border">
                <h3 className="font-heading font-semibold mb-4">Dernières transactions</h3>
                <div className="space-y-3">
                  {transactions.slice(0, 5).map((tx, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="font-medium">{tx.user_email}</p>
                        <p className="text-xs text-muted">{new Date(tx.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <span className="font-semibold text-green-500">+{tx.amount}€</span>
                    </div>
                  ))}
                  {transactions.length === 0 && (
                    <p className="text-sm text-muted">Aucune transaction récente</p>
                  )}
                </div>
              </Card>
            </div>

            {/* Reset Stats Button */}
            <Card className="p-6 border-border border-red-500/30 bg-red-500/5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-red-400">Zone de maintenance</h3>
                  <p className="text-sm text-muted mt-1">
                    Réinitialiser les données de test (transactions, abonnements)
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  onClick={async () => {
                    if (!confirm('⚠️ Cette action va supprimer TOUTES les transactions et abonnements de test.\n\nLes comptes utilisateurs seront conservés.\n\nContinuer ?')) return;
                    
                    const token = localStorage.getItem('token');
                    try {
                      const res = await axios.post(`${API}/admin/reset/stats`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(res.data.message);
                      loadData();
                    } catch (error) {
                      toast.error('Erreur lors de la réinitialisation');
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Réinitialiser les stats
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                  <Input
                    placeholder="Rechercher par email ou nom..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'free', 'paid', 'pro', 'agency', 'avatar_trial'].map(filter => (
                  <Button
                    key={filter}
                    variant={filterPlan === filter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterPlan(filter)}
                  >
                    {filter === 'all' ? 'Tous' : 
                     filter === 'free' ? 'Gratuits' :
                     filter === 'paid' ? 'Payants' :
                     filter === 'pro' ? 'Pro' : 
                     filter === 'agency' ? 'Agence' : 'Leads Avatar'}
                  </Button>
                ))}
              </div>
              <Button 
                onClick={() => setShowGiftModal(true)}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              >
                <Gift className="w-4 h-4 mr-2" />
                Offrir un abonnement
              </Button>
              <Button 
                onClick={() => setShowSearchUserModal(true)}
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              >
                <Search className="w-4 h-4 mr-2" />
                Rechercher & Renvoyer PDFs
              </Button>
            </div>

            {/* Users Table */}
            <Card className="border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-card border-b border-border">
                    <tr>
                      <th className="text-left p-4 font-medium text-muted">Utilisateur</th>
                      <th className="text-left p-4 font-medium text-muted">Profil</th>
                      <th className="text-left p-4 font-medium text-muted">Plan</th>
                      <th className="text-left p-4 font-medium text-muted">Études</th>
                      <th className="text-left p-4 font-medium text-muted">Inscription</th>
                      <th className="text-left p-4 font-medium text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, i) => (
                      <tr key={user.id || i} className="border-b border-border/50 hover:bg-card/50">
                        <td className="p-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{user.name || 'Sans nom'}</p>
                              {user.source === 'avatar_trial' && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-pink-500/20 text-pink-400" title="Lead venant du test Avatar gratuit">
                                  Avatar
                                </span>
                              )}
                              {user.avatar_trial?.completed && user.source !== 'avatar_trial' && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-500/20 text-green-400" title="A complété le test Avatar">
                                  ✓ Avatar
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted">{user.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          {user.diagnostic?.profile ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.diagnostic.profile === 'Plafonneur' ? 'bg-orange-500/20 text-orange-400' :
                              user.diagnostic.profile === 'Dispersé' ? 'bg-purple-500/20 text-purple-400' :
                              user.diagnostic.profile === 'Prédictible' ? 'bg-green-500/20 text-green-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {user.diagnostic.profile}
                              {user.diagnostic.score && ` (${user.diagnostic.score}/100)`}
                            </span>
                          ) : (
                            <span className="text-muted text-sm">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          {getPlanBadge(user.subscription?.plan)}
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <p>{user.studies_count} étude(s)</p>
                            <p className="text-muted">{user.subscription?.studies_remaining || 0} restante(s)</p>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowUserModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Gérer
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <div className="p-8 text-center text-muted">
                  Aucun utilisateur trouvé
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-8 border-border max-w-2xl">
              <h2 className="text-xl font-heading font-bold mb-2">Export pour CRM</h2>
              <p className="text-muted mb-6">
                Exportez vos utilisateurs au format CSV compatible avec LearnyBox et autres CRM.
              </p>

              <div className="space-y-4">
                <Card className="p-4 border-border hover:border-primary transition-colors cursor-pointer" onClick={() => handleExport('all')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium">Tous les utilisateurs</p>
                        <p className="text-sm text-muted">Gratuits + Payants</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-muted" />
                  </div>
                </Card>

                <Card className="p-4 border-border hover:border-primary transition-colors cursor-pointer" onClick={() => handleExport('free')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserPlus className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium">Utilisateurs gratuits</p>
                        <p className="text-sm text-muted">Testeurs uniquement</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-muted" />
                  </div>
                </Card>

                <Card className="p-4 border-border hover:border-primary transition-colors cursor-pointer" onClick={() => handleExport('paid')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Crown className="w-5 h-5 text-highlight" />
                      <div>
                        <p className="font-medium">Clients payants</p>
                        <p className="text-sm text-muted">Pro + Agence</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-muted" />
                  </div>
                </Card>

                <Card className="p-4 border-border hover:border-primary transition-colors cursor-pointer" onClick={() => handleExport('pro')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium">Clients Pro</p>
                        <p className="text-sm text-muted">Plan Professionnel uniquement</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-muted" />
                  </div>
                </Card>

                <Card className="p-4 border-border hover:border-primary transition-colors cursor-pointer" onClick={() => handleExport('agency')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-accent" />
                      <div>
                        <p className="font-medium">Clients Agence</p>
                        <p className="text-sm text-muted">Plan Agence uniquement</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-muted" />
                  </div>
                </Card>
              </div>

              <div className="mt-6 p-4 bg-card rounded-lg">
                <h4 className="font-medium mb-2">Format du fichier CSV</h4>
                <p className="text-sm text-muted">
                  Le fichier contient : email, prénom, nom, plan, statut, date d'inscription, tags.
                  <br />
                  Les tags incluent automatiquement le plan pour faciliter la segmentation dans LearnyBox.
                </p>
              </div>
            </Card>
          </motion.div>
        )}


        {/* Studies Tab */}
        {activeTab === 'studies' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Études Boost-StrategIA</h2>
                <p className="text-muted">Études marketing en cours et complétées</p>
              </div>
              <Button onClick={loadStudiesList} variant="outline">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingStudiesList ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-card border-b border-border">
                    <tr>
                      <th className="text-left p-4 font-medium text-muted">Client</th>
                      <th className="text-left p-4 font-medium text-muted">Étude</th>
                      <th className="text-left p-4 font-medium text-muted">Progression</th>
                      <th className="text-left p-4 font-medium text-muted">Messages</th>
                      <th className="text-left p-4 font-medium text-muted">Date</th>
                      <th className="text-left p-4 font-medium text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loadingStudiesList ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted">
                          Chargement...
                        </td>
                      </tr>
                    ) : studiesList.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted">
                          Aucune étude
                        </td>
                      </tr>
                    ) : (
                      studiesList.map((study) => (
                        <tr key={study.id} className="hover:bg-card/50">
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{study.user_name || 'Sans nom'}</p>
                              <p className="text-sm text-muted">{study.user_email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="text-sm truncate max-w-[200px]">{study.name || 'Sans titre'}</p>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-border rounded-full h-2">
                                <div 
                                  className="bg-primary h-2 rounded-full" 
                                  style={{ width: `${Math.min((study.current_step / 47) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-sm text-muted">{study.current_step}/47</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm">
                            {study.messages_count || 0}
                          </td>
                          <td className="p-4 text-sm text-muted">
                            {study.created_at ? new Date(study.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            }) : 'N/A'}
                          </td>
                          <td className="p-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendStudyPdf(study.id, study.user_email)}
                              disabled={sendingStudyPdf === study.id || study.messages_count === 0}
                              title={study.messages_count === 0 ? 'Aucun message dans l\'étude' : 'Envoyer le PDF par email'}
                            >
                              {sendingStudyPdf === study.id ? (
                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 mr-1" />
                              )}
                              Envoyer PDF
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Cancellations Tab */}
        {activeTab === 'cancellations' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="cancellations-tab"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Annulations d'abonnement</h2>
                <p className="text-muted">Clients en cours d'annulation, motifs et clients sauvés par l'offre -40%</p>
              </div>
              <Button onClick={loadCancellations} variant="outline" data-testid="refresh-cancellations">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingCancellations ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card className="p-4">
                <div className="text-sm text-muted">En cours d'annulation</div>
                <div className="text-3xl font-heading font-bold text-red-400" data-testid="total-cancellations">
                  {cancellationsData?.total_cancellations ?? 0}
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-muted">Clients sauvés (offre -40%)</div>
                <div className="text-3xl font-heading font-bold text-green-400" data-testid="total-saved">
                  {cancellationsData?.total_saved ?? 0}
                </div>
              </Card>
            </div>

            <Card className="overflow-hidden mb-6">
              <div className="p-4 border-b border-border font-medium">Annulations en cours</div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/10 text-sm text-muted">
                    <tr>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-left p-3">Annulé le</th>
                      <th className="text-left p-3">Accès jusqu'au</th>
                      <th className="text-left p-3">Motif</th>
                    </tr>
                  </thead>
                  <tbody data-testid="cancellations-table">
                    {(cancellationsData?.cancellations || []).length === 0 ? (
                      <tr><td colSpan="5" className="p-6 text-center text-muted">Aucune annulation en cours 🎉</td></tr>
                    ) : (
                      cancellationsData.cancellations.map((c, idx) => (
                        <tr key={idx} className="border-t border-border text-sm">
                          <td className="p-3">
                            <div className="font-medium">{c.name || 'Sans nom'}</div>
                            <div className="text-muted text-xs">{c.email}</div>
                          </td>
                          <td className="p-3 capitalize">{c.plan}</td>
                          <td className="p-3">{c.canceled_at ? new Date(c.canceled_at).toLocaleDateString('fr-FR') : '-'}</td>
                          <td className="p-3">{c.access_until ? new Date(c.access_until).toLocaleDateString('fr-FR') : '-'}</td>
                          <td className="p-3 text-muted italic">{c.reason || 'Non renseigné'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="p-4 border-b border-border font-medium">Clients sauvés par l'offre -40% (3 mois)</div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/10 text-sm text-muted">
                    <tr>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Offre acceptée le</th>
                    </tr>
                  </thead>
                  <tbody data-testid="retention-saved-table">
                    {(cancellationsData?.retention_saved || []).length === 0 ? (
                      <tr><td colSpan="2" className="p-6 text-center text-muted">Aucun client n'a encore accepté l'offre</td></tr>
                    ) : (
                      cancellationsData.retention_saved.map((s, idx) => (
                        <tr key={idx} className="border-t border-border text-sm">
                          <td className="p-3">
                            <div className="font-medium">{s.name || 'Sans nom'}</div>
                            <div className="text-muted text-xs">{s.email}</div>
                          </td>
                          <td className="p-3">{s.retention_offer_at ? new Date(s.retention_offer_at).toLocaleDateString('fr-FR') : '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Diagnostics Tab */}
        {activeTab === 'diagnostics' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Sessions de Diagnostic</h2>
                <p className="text-muted">Score de Scalabilité - Sessions complétées</p>
              </div>
              <Button onClick={loadDiagnosticSessions} variant="outline">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingDiagnostics ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-card border-b border-border">
                    <tr>
                      <th className="text-left p-4 font-medium text-muted">Contact</th>
                      <th className="text-left p-4 font-medium text-muted">Profil</th>
                      <th className="text-left p-4 font-medium text-muted">Score</th>
                      <th className="text-left p-4 font-medium text-muted">Date</th>
                      <th className="text-left p-4 font-medium text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loadingDiagnostics ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-muted">
                          Chargement...
                        </td>
                      </tr>
                    ) : diagnosticSessions.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-muted">
                          Aucune session complétée
                        </td>
                      </tr>
                    ) : (
                      diagnosticSessions.map((session) => (
                        <tr key={session.session_id} className="hover:bg-card/50">
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{session.first_name || 'Sans nom'}</p>
                              <p className="text-sm text-muted">{session.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.profile === 'Plafonneur' ? 'bg-orange-500/20 text-orange-400' :
                              session.profile === 'Dispersé' ? 'bg-purple-500/20 text-purple-400' :
                              session.profile === 'Prédictible' ? 'bg-green-500/20 text-green-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {session.profile || 'N/A'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="font-medium">{session.score || 'N/A'}/100</span>
                          </td>
                          <td className="p-4 text-sm text-muted">
                            {session.completed_at ? new Date(session.completed_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </td>
                          <td className="p-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resendDiagnosticPdf(session.session_id, session.email)}
                              disabled={resendingPdf === session.session_id}
                            >
                              {resendingPdf === session.session_id ? (
                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 mr-1" />
                              )}
                              Renvoyer PDF
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Avatar Trials Tab */}
        {activeTab === 'avatarTrials' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Essais Gratuits (Avatar)</h2>
                <p className="text-muted">Utilisateurs ayant commencé l'essai gratuit des 3 étapes</p>
              </div>
              <Button variant="outline" onClick={loadAvatarTrials} disabled={loadingAvatarTrials}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingAvatarTrials ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            <Card>
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-4 text-sm text-muted">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {avatarTrials.length} essais
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {avatarTrials.filter(t => t.completed).length} complétés
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium">Email</th>
                      <th className="text-left p-4 text-sm font-medium">Prénom</th>
                      <th className="text-left p-4 text-sm font-medium">Étape</th>
                      <th className="text-left p-4 text-sm font-medium">Statut</th>
                      <th className="text-left p-4 text-sm font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingAvatarTrials ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-muted">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                          Chargement...
                        </td>
                      </tr>
                    ) : avatarTrials.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-muted">
                          Aucun essai gratuit pour le moment
                        </td>
                      </tr>
                    ) : (
                      avatarTrials.map((trial, i) => (
                        <tr key={trial.session_id || i} className="border-t border-border hover:bg-muted/20">
                          <td className="p-4">
                            <span className="font-medium">{trial.email}</span>
                          </td>
                          <td className="p-4">{trial.first_name || '-'}</td>
                          <td className="p-4">
                            <span className="px-2 py-1 rounded bg-primary/20 text-primary text-sm">
                              Étape {trial.current_step || 0}/3
                            </span>
                          </td>
                          <td className="p-4">
                            {trial.completed ? (
                              <span className="flex items-center gap-1 text-green-500">
                                <CheckCircle className="w-4 h-4" />
                                Complété
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-yellow-500">
                                <Clock className="w-4 h-4" />
                                En cours
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-muted text-sm">
                            {trial.created_at ? new Date(trial.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Offer Quiz Tab - LM Offre */}
        {activeTab === 'offerQuiz' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Quiz "Offre Irrésistible"</h2>
                <p className="text-muted">Leads du quiz d'évaluation d'offre</p>
              </div>
              <Button variant="outline" onClick={loadOfferQuizSessions} disabled={loadingOfferQuiz}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingOfferQuiz ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Card className="p-4 text-center">
                <p className="text-2xl font-bold">{offerQuizStats.total || 0}</p>
                <p className="text-sm text-muted">Total</p>
              </Card>
              <Card className="p-4 text-center border-gray-500/30">
                <p className="text-2xl font-bold" style={{color: '#6b7280'}}>{offerQuizStats.fantome || 0}</p>
                <p className="text-sm text-muted">👻 Fantôme</p>
              </Card>
              <Card className="p-4 text-center border-amber-500/30">
                <p className="text-2xl font-bold" style={{color: '#f59e0b'}}>{offerQuizStats.brouillon || 0}</p>
                <p className="text-sm text-muted">📝 Brouillon</p>
              </Card>
              <Card className="p-4 text-center border-green-500/30">
                <p className="text-2xl font-bold" style={{color: '#10b981'}}>{offerQuizStats.prometteur || 0}</p>
                <p className="text-sm text-muted">🌱 Prometteur</p>
              </Card>
              <Card className="p-4 text-center border-red-500/30">
                <p className="text-2xl font-bold" style={{color: '#ef4444'}}>{offerQuizStats.irresistible || 0}</p>
                <p className="text-sm text-muted">🔥 Irrésistible</p>
              </Card>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium">Email</th>
                      <th className="text-left p-4 text-sm font-medium">Prénom</th>
                      <th className="text-left p-4 text-sm font-medium">Score</th>
                      <th className="text-left p-4 text-sm font-medium">Profil</th>
                      <th className="text-left p-4 text-sm font-medium">Statut</th>
                      <th className="text-left p-4 text-sm font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingOfferQuiz ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                          Chargement...
                        </td>
                      </tr>
                    ) : offerQuizSessions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted">
                          Aucun quiz pour le moment
                        </td>
                      </tr>
                    ) : (
                      offerQuizSessions.map((session, i) => (
                        <tr key={session.session_id || i} className="border-t border-border hover:bg-muted/20">
                          <td className="p-4">
                            <span className="font-medium">{session.email}</span>
                          </td>
                          <td className="p-4">{session.first_name || '-'}</td>
                          <td className="p-4">
                            {session.score ? (
                              <span className="font-bold">{session.score}/60</span>
                            ) : '-'}
                          </td>
                          <td className="p-4">
                            {session.profile === 'fantome' && (
                              <span className="px-2 py-1 rounded text-sm" style={{backgroundColor: '#6b728020', color: '#6b7280'}}>
                                👻 Fantôme
                              </span>
                            )}
                            {session.profile === 'brouillon' && (
                              <span className="px-2 py-1 rounded text-sm" style={{backgroundColor: '#f59e0b20', color: '#f59e0b'}}>
                                📝 Brouillon
                              </span>
                            )}
                            {session.profile === 'prometteur' && (
                              <span className="px-2 py-1 rounded text-sm" style={{backgroundColor: '#10b98120', color: '#10b981'}}>
                                🌱 Prometteur
                              </span>
                            )}
                            {session.profile === 'irresistible' && (
                              <span className="px-2 py-1 rounded text-sm" style={{backgroundColor: '#ef444420', color: '#ef4444'}}>
                                🔥 Irrésistible
                              </span>
                            )}
                            {!session.profile && '-'}
                          </td>
                          <td className="p-4">
                            {session.completed ? (
                              <span className="flex items-center gap-1 text-green-500">
                                <CheckCircle className="w-4 h-4" />
                                Complété
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-yellow-500">
                                <Clock className="w-4 h-4" />
                                En cours
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-muted text-sm">
                            {session.created_at ? new Date(session.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}


        {/* Trial No CB Tab */}
        {activeTab === 'trialNoCB' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold">Essais gratuits sans CB</h2>
                <p className="text-muted text-sm">Utilisateurs en période d'essai (7 jours ou codes promo) sans carte bancaire</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowFixPromoModal(true)} 
                  variant="outline" 
                  className="gap-2 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                >
                  <Settings className="w-4 h-4" />
                  Corriger un utilisateur
                </Button>
                <Button onClick={loadTrialNoCBUsers} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <Card className="p-4 border-primary/30 bg-primary/5">
                <p className="text-sm text-muted">Total inscrits</p>
                <p className="text-2xl font-bold text-primary">{trialNoCBUsers.length}</p>
              </Card>
              <Card className="p-4 border-blue-500/30 bg-blue-500/5">
                <p className="text-sm text-muted">Via code promo</p>
                <p className="text-2xl font-bold text-blue-400">
                  {trialNoCBUsers.filter(u => u.promo_code_used).length}
                </p>
              </Card>
              <Card className="p-4 border-amber-500/30 bg-amber-500/5">
                <p className="text-sm text-muted">Essai en cours</p>
                <p className="text-2xl font-bold text-amber-400">
                  {trialNoCBUsers.filter(u => u.status === 'trial_no_cb' || u.status === 'promo_no_cb').length}
                </p>
              </Card>
              <Card className="p-4 border-red-500/30 bg-red-500/5">
                <p className="text-sm text-muted">Expirés</p>
                <p className="text-2xl font-bold text-red-400">
                  {trialNoCBUsers.filter(u => u.status === 'trial_expired').length}
                </p>
              </Card>
              <Card className="p-4 border-green-500/30 bg-green-500/5">
                <p className="text-sm text-muted">Convertis</p>
                <p className="text-2xl font-bold text-green-400">
                  {trialNoCBUsers.filter(u => u.status === 'active' || u.status === 'trialing').length}
                </p>
              </Card>
            </div>

            <Card className="border-border">
              {loadingTrialNoCB ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-muted">Chargement...</p>
                </div>
              ) : trialNoCBUsers.length === 0 ? (
                <div className="p-8 text-center">
                  <Clock className="w-12 h-12 text-muted mx-auto mb-4" />
                  <p className="text-muted">Aucun utilisateur en essai sans CB pour le moment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium">Utilisateur</th>
                        <th className="text-left p-4 text-sm font-medium">Code promo</th>
                        <th className="text-left p-4 text-sm font-medium">Inscription</th>
                        <th className="text-left p-4 text-sm font-medium">Fin de l'essai</th>
                        <th className="text-left p-4 text-sm font-medium">Jours restants</th>
                        <th className="text-left p-4 text-sm font-medium">Statut</th>
                        <th className="text-left p-4 text-sm font-medium">Études créées</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trialNoCBUsers.map((user, idx) => {
                        const trialEnd = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
                        const now = new Date();
                        const daysRemaining = trialEnd ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)) : 0;
                        const isExpired = daysRemaining <= 0;
                        const isConverted = user.status === 'active' || user.status === 'trialing';
                        
                        return (
                          <tr key={user.id || idx} className="border-t border-border hover:bg-muted/20">
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{user.name || 'Sans nom'}</p>
                                <p className="text-xs text-muted">{user.email}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              {user.promo_code_used ? (
                                <span className="px-2 py-1 rounded-full text-xs font-mono font-medium bg-blue-500/20 text-blue-400">
                                  {user.promo_code_used}
                                </span>
                              ) : (
                                <span className="text-xs text-muted">7j gratuit</span>
                              )}
                            </td>
                            <td className="p-4 text-sm">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              }) : '-'}
                            </td>
                            <td className="p-4 text-sm">
                              {trialEnd ? trialEnd.toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              }) : '-'}
                            </td>
                            <td className="p-4">
                              {isConverted ? (
                                <span className="text-green-400 font-medium">Converti</span>
                              ) : isExpired ? (
                                <span className="text-red-400 font-medium">Expiré</span>
                              ) : (
                                <span className={`font-medium ${daysRemaining <= 2 ? 'text-amber-400' : 'text-primary'}`}>
                                  {daysRemaining} jour{daysRemaining > 1 ? 's' : ''}
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              {isConverted ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                  Payant
                                </span>
                              ) : user.status === 'trial_expired' || isExpired ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                                  Expiré
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                                  En essai
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-sm">
                              {user.studies_count || 0}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </motion.div>
        )}


        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Emails Planifiés</h2>
                <p className="text-muted">Séquence d'emails automatiques pour les utilisateurs gratuits</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => loadScheduledEmails(emailStatusFilter)}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
                <Button onClick={triggerEmailProcessing}>
                  <Mail className="w-4 h-4 mr-2" />
                  Traiter maintenant
                </Button>
              </div>
            </div>

            {/* Email Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Card className="p-4 text-center cursor-pointer hover:border-primary transition-colors"
                    onClick={() => { setEmailStatusFilter('all'); loadScheduledEmails('all'); }}>
                <div className="text-2xl font-bold">{emailStats.total}</div>
                <div className="text-sm text-muted">Total</div>
              </Card>
              <Card className={`p-4 text-center cursor-pointer hover:border-primary transition-colors ${emailStatusFilter === 'pending' ? 'border-primary' : ''}`}
                    onClick={() => { setEmailStatusFilter('pending'); loadScheduledEmails('pending'); }}>
                <Clock className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
                <div className="text-2xl font-bold text-yellow-500">{emailStats.pending}</div>
                <div className="text-sm text-muted">En attente</div>
              </Card>
              <Card className={`p-4 text-center cursor-pointer hover:border-primary transition-colors ${emailStatusFilter === 'sent' ? 'border-primary' : ''}`}
                    onClick={() => { setEmailStatusFilter('sent'); loadScheduledEmails('sent'); }}>
                <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-500" />
                <div className="text-2xl font-bold text-green-500">{emailStats.sent}</div>
                <div className="text-sm text-muted">Envoyés</div>
              </Card>
              <Card className={`p-4 text-center cursor-pointer hover:border-primary transition-colors ${emailStatusFilter === 'failed' ? 'border-primary' : ''}`}
                    onClick={() => { setEmailStatusFilter('failed'); loadScheduledEmails('failed'); }}>
                <XCircle className="w-5 h-5 mx-auto mb-1 text-red-500" />
                <div className="text-2xl font-bold text-red-500">{emailStats.failed}</div>
                <div className="text-sm text-muted">Échoués</div>
              </Card>
              <Card className={`p-4 text-center cursor-pointer hover:border-primary transition-colors ${emailStatusFilter === 'cancelled' ? 'border-primary' : ''}`}
                    onClick={() => { setEmailStatusFilter('cancelled'); loadScheduledEmails('cancelled'); }}>
                <Ban className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                <div className="text-2xl font-bold text-gray-500">{emailStats.cancelled}</div>
                <div className="text-sm text-muted">Annulés</div>
              </Card>
            </div>

            {/* Info Banner */}
            <Card className="p-4 mb-6 bg-primary/10 border-primary/30">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-semibold text-primary">Séquence emails utilisateurs gratuits</h4>
                  <ul className="text-sm text-muted mt-2 space-y-1">
                    <li>• <strong>Email #1</strong> (+5 min) : Lien vers l'étude</li>
                    <li>• <strong>Email #2</strong> (+12h) : Témoignages + Code BIENVENUE (20%)</li>
                    <li>• <strong>Email #3</strong> (J+2) : Relance "Dernière chance"</li>
                    <li>• <strong>Email #4</strong> (J+7) : Code STRATEGIE10 (10%)</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Emails Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-card border-b border-border">
                    <tr>
                      <th className="text-left p-4 font-medium">Utilisateur</th>
                      <th className="text-left p-4 font-medium">Email #</th>
                      <th className="text-left p-4 font-medium">Template</th>
                      <th className="text-left p-4 font-medium">Planifié pour</th>
                      <th className="text-left p-4 font-medium">Statut</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {scheduledEmails.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted">
                          <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p>Aucun email planifié</p>
                          <p className="text-sm">Les emails seront créés automatiquement quand un utilisateur gratuit terminera l'étape 3</p>
                        </td>
                      </tr>
                    ) : (
                      scheduledEmails.map((email, idx) => (
                        <tr key={idx} className="hover:bg-card/50">
                          <td className="p-4">
                            <div className="font-medium">{email.user_name}</div>
                            <div className="text-sm text-muted">{email.user_email}</div>
                          </td>
                          <td className="p-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold">
                              {email.email_number}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm">
                              {email.template === 'free_trial_complete' && 'Lien étude'}
                              {email.template === 'testimonials' && 'Témoignages + BIENVENUE'}
                              {email.template === 'last_chance' && 'Dernière chance'}
                              {email.template === 'final_offer' && 'STRATEGIE10'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="text-sm">
                              {new Date(email.scheduled_for).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="p-4">
                            {email.status === 'pending' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-500/10 text-yellow-500">
                                <Clock className="w-3 h-3" /> En attente
                              </span>
                            )}
                            {email.status === 'sent' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/10 text-green-500">
                                <CheckCircle className="w-3 h-3" /> Envoyé
                              </span>
                            )}
                            {email.status === 'failed' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/10 text-red-500">
                                <XCircle className="w-3 h-3" /> Échoué
                              </span>
                            )}
                            {email.status === 'cancelled' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-500/10 text-gray-500">
                                <Ban className="w-3 h-3" /> Annulé
                              </span>
                            )}
                          </td>
                          <td className="p-4">
                            {email.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={() => cancelUserEmails(email.user_id)}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Annuler
                              </Button>
                            )}
                            {email.error && (
                              <span className="text-xs text-red-500" title={email.error}>
                                Erreur
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold">Codes Promo</h2>
                <p className="text-muted">Créez et gérez vos codes de réduction Stripe</p>
              </div>
              <Button onClick={() => setShowPromoModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouveau code
              </Button>
            </div>

            <Card className="border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-card">
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-sm font-medium text-muted">Code</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Réduction</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Plans</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Utilisations</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Expiration</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Stripe</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Statut</th>
                      <th className="text-left p-4 text-sm font-medium text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promo) => (
                      <tr key={promo.id} className="border-b border-border hover:bg-card/50">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4 text-primary" />
                            <span className="font-mono font-bold">{promo.code}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          {promo.discount_type === 'percent' ? (
                            <span className="flex items-center gap-1">
                              <Percent className="w-4 h-4 text-highlight" />
                              {promo.discount_value}%
                            </span>
                          ) : promo.discount_type === 'months_free' ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-accent" />
                              {promo.discount_value} mois (CB)
                            </span>
                          ) : promo.discount_type === 'no_cb' ? (
                            <span className="flex items-center gap-1 text-blue-400">
                              <Gift className="w-4 h-4" />
                              {promo.discount_value} jours sans CB
                            </span>
                          ) : (
                            <span>{promo.discount_value}</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1 flex-wrap">
                            {promo.applicable_plans?.map(plan => (
                              <span key={plan} className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                                {plan}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-muted">
                          {promo.times_used}{promo.max_uses ? ` / ${promo.max_uses}` : ''}
                        </td>
                        <td className="p-4 text-muted text-sm">
                          {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString('fr-FR') : 'Illimité'}
                        </td>
                        <td className="p-4">
                          {promo.stripe_coupon_id ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Lié
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs bg-orange-500/20 text-orange-400 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Non lié
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            promo.is_active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {promo.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {!promo.stripe_coupon_id && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-orange-400 border-orange-400/50 hover:bg-orange-400/10"
                                onClick={() => {
                                  setSelectedPromoForLink(promo);
                                  setStripeCouponId('');
                                  setShowLinkStripeModal(true);
                                }}
                              >
                                Lier Stripe
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleTogglePromoCode(promo.id, promo.is_active)}
                            >
                              {promo.is_active ? 'Désactiver' : 'Activer'}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeletePromoCode(promo.id, promo.code)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {promoCodes.length === 0 && (
                <div className="p-8 text-center text-muted">
                  Aucun code promo créé
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </main>

      {/* Promo Code Creation Modal */}
      {showPromoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold">Créer un code promo</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPromoModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Code */}
              <div>
                <label className="text-sm text-muted block mb-2">Code (ex: BIENVENUE)</label>
                <Input
                  value={newPromoCode.code}
                  onChange={(e) => setNewPromoCode({...newPromoCode, code: e.target.value.toUpperCase()})}
                  placeholder="MONCODE"
                  className="font-mono"
                />
              </div>

              {/* Discount Type */}
              <div>
                <label className="text-sm text-muted block mb-2">Type de code</label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={newPromoCode.discount_type === 'percent' ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setNewPromoCode({...newPromoCode, discount_type: 'percent', discount_value: 10})}
                  >
                    <Percent className="w-4 h-4 mr-1" />
                    % Réduction
                  </Button>
                  <Button
                    type="button"
                    variant={newPromoCode.discount_type === 'months_free' ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setNewPromoCode({...newPromoCode, discount_type: 'months_free', discount_value: 1})}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Mois offerts
                  </Button>
                  <Button
                    type="button"
                    variant={newPromoCode.discount_type === 'no_cb' ? 'default' : 'outline'}
                    className="flex-1 bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30"
                    onClick={() => setNewPromoCode({...newPromoCode, discount_type: 'no_cb', discount_value: 90})}
                  >
                    <Gift className="w-4 h-4 mr-1" />
                    Sans CB
                  </Button>
                </div>
                {newPromoCode.discount_type === 'months_free' && (
                  <p className="text-xs text-muted mt-2">
                    ℹ️ CB requise mais non débitée pendant la période gratuite
                  </p>
                )}
                {newPromoCode.discount_type === 'no_cb' && (
                  <p className="text-xs text-blue-400 mt-2">
                    ✨ Accès complet SANS carte bancaire - idéal pour les partenaires
                  </p>
                )}
              </div>

              {/* Discount Value */}
              <div>
                <label className="text-sm text-muted block mb-2">
                  {newPromoCode.discount_type === 'percent' && 'Pourcentage de réduction'}
                  {newPromoCode.discount_type === 'months_free' && 'Nombre de mois offerts (CB requise)'}
                  {newPromoCode.discount_type === 'no_cb' && 'Durée d\'accès gratuit (jours)'}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={newPromoCode.discount_value}
                    onChange={(e) => setNewPromoCode({...newPromoCode, discount_value: parseInt(e.target.value) || 0})}
                    min={1}
                    max={newPromoCode.discount_type === 'percent' ? 100 : newPromoCode.discount_type === 'months_free' ? 12 : 365}
                    className="w-24"
                  />
                  <span className="text-muted">
                    {newPromoCode.discount_type === 'percent' && '%'}
                    {newPromoCode.discount_type === 'months_free' && 'mois'}
                    {newPromoCode.discount_type === 'no_cb' && 'jours'}
                  </span>
                  {newPromoCode.discount_type === 'no_cb' && (
                    <span className="text-xs text-muted">
                      (90 jours = 3 mois)
                    </span>
                  )}
                </div>
              </div>

              {/* Studies Limit (only for no_cb) */}
              {newPromoCode.discount_type === 'no_cb' && (
                <div>
                  <label className="text-sm text-muted block mb-2">Nombre d'études autorisées (optionnel)</label>
                  <Input
                    type="number"
                    value={newPromoCode.studies_limit || ''}
                    onChange={(e) => setNewPromoCode({...newPromoCode, studies_limit: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Défaut selon le plan (Pro=2, Agency=5)"
                    min={1}
                  />
                </div>
              )}

              {/* Max Uses */}
              <div>
                <label className="text-sm text-muted block mb-2">Nombre max d'utilisations (optionnel)</label>
                <Input
                  type="number"
                  value={newPromoCode.max_uses || ''}
                  onChange={(e) => setNewPromoCode({...newPromoCode, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Illimité"
                  min={1}
                />
              </div>

              {/* Expiration */}
              <div>
                <label className="text-sm text-muted block mb-2">Date d'expiration (optionnel)</label>
                <Input
                  type="date"
                  value={newPromoCode.expires_at}
                  onChange={(e) => setNewPromoCode({...newPromoCode, expires_at: e.target.value})}
                />
              </div>

              {/* Applicable Plans */}
              <div>
                <label className="text-sm text-muted block mb-2">Plans concernés</label>
                <div className="flex gap-2">
                  {['pro', 'agency'].map(plan => (
                    <Button
                      key={plan}
                      type="button"
                      variant={newPromoCode.applicable_plans.includes(plan) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const plans = newPromoCode.applicable_plans.includes(plan)
                          ? newPromoCode.applicable_plans.filter(p => p !== plan)
                          : [...newPromoCode.applicable_plans, plan];
                        setNewPromoCode({...newPromoCode, applicable_plans: plans});
                      }}
                    >
                      {plan === 'pro' ? 'Pro' : 'Agence'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowPromoModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreatePromoCode}>
                <Plus className="w-4 h-4 mr-2" />
                Créer le code
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Link Stripe Modal */}
      {showLinkStripeModal && selectedPromoForLink && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 max-w-lg w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold">Lier un coupon Stripe</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowLinkStripeModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <p className="text-sm text-orange-300 mb-2">
                  <strong>Important :</strong> Pour que le code <span className="font-mono font-bold">{selectedPromoForLink.code}</span> fonctionne avec Stripe, vous devez :
                </p>
                <ol className="text-sm text-muted list-decimal list-inside space-y-1">
                  <li>Aller dans votre <a href="https://dashboard.stripe.com/coupons" target="_blank" rel="noreferrer" className="text-primary hover:underline">Dashboard Stripe → Coupons</a></li>
                  <li>Créer un nouveau coupon avec {selectedPromoForLink.discount_value}% de réduction</li>
                  <li>Copier l'ID du coupon (ex: XXXX_abc123)</li>
                  <li>Le coller ci-dessous</li>
                </ol>
              </div>
              
              <div>
                <label className="text-sm text-muted block mb-2">ID du Coupon Stripe</label>
                <Input
                  value={stripeCouponId}
                  onChange={(e) => setStripeCouponId(e.target.value)}
                  placeholder="Ex: BOOST_BIENVENUE_abc123"
                  className="font-mono"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowLinkStripeModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleLinkStripe} disabled={!stripeCouponId.trim()}>
                Lier le coupon
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Promo Code Confirmation Modal */}
      {showDeletePromoModal && promoToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-heading font-bold">Supprimer le code promo</h3>
            </div>
            
            <p className="text-muted mb-6">
              Êtes-vous sûr de vouloir supprimer le code <span className="font-mono font-bold text-white">{promoToDelete.code}</span> ?
              Cette action est irréversible.
            </p>
            
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeletePromoModal(false);
                  setPromoToDelete(null);
                }}
              >
                Annuler
              </Button>
              <Button 
                variant="destructive"
                className="bg-red-500 hover:bg-red-600"
                onClick={confirmDeletePromoCode}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 max-w-md w-full"
          >
            <h3 className="text-lg font-heading font-bold mb-4">Gérer l'utilisateur</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted">Email</p>
                <p className="font-medium">{selectedUser.email}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted">Plan actuel</p>
                <p className="font-medium">{selectedUser.subscription?.plan || 'Gratuit'}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted">Études restantes</p>
                <p className="font-medium">{selectedUser.subscription?.studies_remaining || 0}</p>
              </div>

              <hr className="border-border" />

              {/* Add studies */}
              <div>
                <p className="text-sm text-muted mb-2">Offrir des études</p>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="1"
                    value={addStudiesAmount}
                    onChange={(e) => setAddStudiesAmount(parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                  <Button onClick={() => handleAddStudies(selectedUser.id)} className="flex-1">
                    <Gift className="w-4 h-4 mr-2" />
                    Ajouter {addStudiesAmount} étude(s)
                  </Button>
                </div>
              </div>

              {/* Change plan */}
              <div>
                <p className="text-sm text-muted mb-2">Changer de plan</p>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => handleChangePlan(selectedUser.id, 'free')}>
                    Gratuit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleChangePlan(selectedUser.id, 'pro')}>
                    Pro
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleChangePlan(selectedUser.id, 'agency')}>
                    Agence
                  </Button>
                </div>
              </div>

              <hr className="border-border" />

              {/* View/Manage Studies */}
              <div>
                <p className="text-sm text-muted mb-2">Données client</p>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => loadUserStudies(selectedUser.id)}
                    disabled={loadingStudies}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    {loadingStudies ? 'Chargement...' : 'Voir les études'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                    onClick={() => {
                      setDeleteTarget({ type: 'all', userId: selectedUser.id, email: selectedUser.email });
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer toutes les données
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowUserModal(false)}>
                Fermer
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Studies Modal */}
      {showStudiesModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold">
                Études de {selectedUser.email}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowStudiesModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {userStudies.length === 0 ? (
              <p className="text-muted text-center py-8">Aucune étude pour cet utilisateur</p>
            ) : (
              <div className="space-y-3">
                {userStudies.map((study) => (
                  <Card key={study.id} className="p-4 border-border">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-primary" />
                          <span className="font-medium">Étude #{study.id?.slice(-6)}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                            Étape {study.current_step || 0}/47
                          </span>
                        </div>
                        <p className="text-sm text-muted">
                          Créée le {study.created_at ? new Date(study.created_at).toLocaleDateString('fr-FR') : '-'}
                        </p>
                        {study.onboarding_data && (
                          <div className="mt-2 text-xs text-muted">
                            <p><strong>Secteur:</strong> {study.onboarding_data.industry || '-'}</p>
                            <p><strong>Cible:</strong> {study.onboarding_data.target_audience || '-'}</p>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                        onClick={() => {
                          setDeleteTarget({ type: 'study', userId: selectedUser.id, studyId: study.id });
                          setShowDeleteConfirm(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowStudiesModal(false)}>
                Fermer
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-red-500/30 rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-heading font-bold text-red-400">
                Confirmer la suppression
              </h3>
            </div>
            
            <p className="text-muted mb-6">
              {deleteTarget.type === 'all' ? (
                <>
                  Êtes-vous sûr de vouloir supprimer <strong>TOUTES les données</strong> de l'utilisateur <strong>{deleteTarget.email}</strong> ?
                  <br /><br />
                  Cette action est <strong>irréversible</strong> et supprimera :
                  <ul className="list-disc list-inside mt-2 text-sm">
                    <li>Le compte utilisateur</li>
                    <li>Toutes les études</li>
                    <li>Tout l'historique des conversations</li>
                    <li>Les réponses au module Légende</li>
                  </ul>
                </>
              ) : (
                <>
                  Êtes-vous sûr de vouloir supprimer cette étude ?
                  <br />
                  Cette action est <strong>irréversible</strong>.
                </>
              )}
            </p>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteTarget(null);
              }}>
                Annuler
              </Button>
              <Button 
                className="bg-red-500 hover:bg-red-600"
                onClick={() => {
                  if (deleteTarget.type === 'all') {
                    handleDeleteAllUserData(deleteTarget.userId);
                  } else {
                    handleDeleteStudy(deleteTarget.userId, deleteTarget.studyId);
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer définitivement
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Gift Subscription Modal */}
      {showGiftModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-pink-500/30 rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                  <Gift className="w-5 h-5 text-pink-400" />
                </div>
                <h3 className="text-lg font-heading font-bold">Offrir un abonnement</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowGiftModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-sm text-muted mb-6">
              L'utilisateur doit d'abord créer un compte gratuit (sans CB). Ensuite, vous pouvez lui offrir un accès Pro ou Agence.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted block mb-2">Email de l'utilisateur *</label>
                <Input
                  value={giftData.email}
                  onChange={(e) => setGiftData({...giftData, email: e.target.value})}
                  placeholder="influenceur@email.com"
                  type="email"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted block mb-2">Plan à offrir</label>
                <div className="flex gap-2">
                  <Button
                    variant={giftData.plan === 'pro' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGiftData({...giftData, plan: 'pro'})}
                    className="flex-1"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Pro
                  </Button>
                  <Button
                    variant={giftData.plan === 'agency' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGiftData({...giftData, plan: 'agency'})}
                    className="flex-1"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Agence
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted block mb-2">Durée (jours)</label>
                  <Input
                    type="number"
                    value={giftData.duration_days}
                    onChange={(e) => setGiftData({...giftData, duration_days: parseInt(e.target.value) || 30})}
                    min="1"
                    max="365"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted block mb-2">Nb d'études</label>
                  <Input
                    type="number"
                    value={giftData.studies}
                    onChange={(e) => setGiftData({...giftData, studies: parseInt(e.target.value) || 5})}
                    min="1"
                    max="50"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowGiftModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleGiftSubscription}
                disabled={giftLoading || !giftData.email.trim()}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              >
                {giftLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Attribution...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Offrir l'abonnement
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal Corriger utilisateur promo */}
      {showFixPromoModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Corriger un utilisateur promo
            </h3>
            <p className="text-sm text-muted mb-4">
              Active l'accès PDF et corrige le nombre d'études pour un utilisateur avec code promo.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted block mb-2">Email de l'utilisateur</label>
                <Input
                  type="email"
                  value={fixPromoData.email}
                  onChange={(e) => setFixPromoData({...fixPromoData, email: e.target.value})}
                  placeholder="client@example.com"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted block mb-2">Nombre d'études autorisées</label>
                <Input
                  type="number"
                  value={fixPromoData.studies_limit}
                  onChange={(e) => setFixPromoData({...fixPromoData, studies_limit: parseInt(e.target.value) || 2})}
                  min="1"
                  max="10"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowFixPromoModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleFixPromoUser}
                disabled={fixPromoLoading || !fixPromoData.email.trim()}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {fixPromoLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Correction...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Corriger l'utilisateur
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal Rechercher utilisateur & Envoyer PDFs */}
      {showSearchUserModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-heading font-bold flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-400" />
                Rechercher un utilisateur
              </h3>
              <Button variant="ghost" size="sm" onClick={() => {
                setShowSearchUserModal(false);
                setSearchUserResult(null);
                setSearchUserEmail('');
              }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Search input */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={searchUserEmail}
                  onChange={(e) => setSearchUserEmail(e.target.value)}
                  placeholder="email@client.com"
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
                />
                <Button 
                  onClick={handleSearchUser}
                  disabled={searchUserLoading || !searchUserEmail.trim()}
                >
                  {searchUserLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Rechercher'}
                </Button>
              </div>

              {/* Results */}
              {searchUserResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* User info */}
                  <Card className="p-4 border-border">
                    <h4 className="font-medium mb-2">{searchUserResult.user?.name || 'Sans nom'}</h4>
                    <p className="text-sm text-muted">{searchUserResult.user?.email}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted">Plan:</span>
                        <span className={`ml-2 font-medium ${
                          searchUserResult.subscription?.plan?.includes('pro') || 
                          searchUserResult.subscription?.plan?.includes('agency') 
                            ? 'text-green-400' 
                            : searchUserResult.subscription?.plan?.includes('trial') 
                              ? 'text-amber-400' 
                              : 'text-muted'
                        }`}>
                          {searchUserResult.subscription?.plan || 'Aucun'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Statut:</span>
                        <span className={`ml-2 font-medium ${
                          searchUserResult.subscription?.status === 'active' ? 'text-green-400' :
                          searchUserResult.subscription?.status === 'trialing' ? 'text-blue-400' :
                          'text-muted'
                        }`}>
                          {searchUserResult.subscription?.status || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Études:</span>
                        <span className="ml-2 font-medium">{searchUserResult.studies?.length || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted">PDF:</span>
                        <span className={`ml-2 font-medium ${searchUserResult.can_download_pdf ? 'text-green-400' : 'text-red-400'}`}>
                          {searchUserResult.pdf_status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border space-y-1 text-sm" data-testid="user-pdf-history">
                      <div>
                        <span className="text-muted">Dernier PDF envoyé:</span>
                        <span className="ml-2 font-medium" data-testid="last-pdf-sent">
                          {searchUserResult.last_pdf_sent_at
                            ? new Date(searchUserResult.last_pdf_sent_at).toLocaleString('fr-FR')
                            : 'Jamais'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted">Dernier téléchargement:</span>
                        <span className="ml-2 font-medium" data-testid="last-pdf-download">
                          {searchUserResult.last_pdf_download_at
                            ? new Date(searchUserResult.last_pdf_download_at).toLocaleString('fr-FR')
                            : 'Jamais'}
                        </span>
                      </div>
                      {searchUserResult.last_pdf_error && (
                        <p className="text-xs text-red-400 p-2 bg-red-500/10 rounded" data-testid="last-pdf-error">
                          ⚠️ Dernier problème ({new Date(searchUserResult.last_pdf_error.created_at).toLocaleString('fr-FR')}) : {searchUserResult.last_pdf_error.error}
                        </p>
                      )}
                    </div>
                  </Card>

                  {/* Studies list */}
                  {searchUserResult.studies && searchUserResult.studies.length > 0 && (
                    <Card className="p-4 border-border">
                      <h4 className="font-medium mb-2">Études ({searchUserResult.studies.length})</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {searchUserResult.studies.map((study, idx) => (
                          <div key={idx} className="text-sm flex justify-between items-center p-2 bg-muted/10 rounded">
                            <span>{study.business_name || `Étude ${idx + 1}`}</span>
                            <span className="text-xs text-muted text-right">
                              {new Date(study.created_at).toLocaleDateString('fr-FR')}
                              <br />
                              <span className={study.last_pdf_sent_at ? 'text-green-400' : 'text-muted'}>
                                {study.last_pdf_sent_at
                                  ? `PDF envoyé le ${new Date(study.last_pdf_sent_at).toLocaleDateString('fr-FR')}`
                                  : 'PDF jamais envoyé'}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {searchUserResult.has_data_issue && (
                      <Button
                        onClick={handleFixPaidUser}
                        variant="outline"
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Corriger les données
                      </Button>
                    )}
                    <Button
                      onClick={handleSendAllPdfs}
                      disabled={sendingAllPdfs || !searchUserResult.studies?.length}
                      className="flex-1 bg-blue-500 hover:bg-blue-600"
                    >
                      {sendingAllPdfs ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Renvoyer tous les PDFs par email
                        </>
                      )}
                    </Button>
                  </div>

                  {searchUserResult.has_data_issue && (
                    <p className="text-xs text-amber-400 text-center p-2 bg-amber-500/10 rounded">
                      ⚠️ {searchUserResult.data_issue_message}
                    </p>
                  )}

                  {!searchUserResult.can_download_pdf && !searchUserResult.has_data_issue && (
                    <p className="text-xs text-amber-400 text-center">
                      ⚠️ Cet utilisateur est en essai 7j sans CB. Allez dans l'onglet "Essai sans CB" pour corriger.
                    </p>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Admin;
